import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import {
  type ApiError,
  type ApiResponse,
  addActionNameToMutationResult,
  useAxiosInstance,
} from 'shared/hooks/utils';
import { z } from 'zod/v4';

const URL = '/invoice_logs';

// The full desired set of logs is sent on every save: existing logs carry their
// `id`, new logs omit it, and any previously-existing log left out is deleted
// server-side. `counter` is the user-facing value; the backend re-adds the
// offset it stripped on read.
const UpdateInvoiceLogsVariablesSchema = z.object({
  invoiceLogs: z.array(
    z.object({
      id: z.string().optional(),
      prefix: z.string(),
      counter: z.number(),
    }),
  ),
});

export type UpdateInvoiceLogsVariables = z.infer<typeof UpdateInvoiceLogsVariablesSchema>;

export async function updateInvoiceLogsFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: UpdateInvoiceLogsVariables,
) {
  const instance = await axiosInstance;

  return instance.put(URL, UpdateInvoiceLogsVariablesSchema.parse(variables));
}

export function useUpdateInvoiceLogs() {
  const client = useQueryClient();
  const axiosInstance = useAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, UpdateInvoiceLogsVariables>({
    mutationFn: variables => updateInvoiceLogsFn(axiosInstance, variables),
    onSuccess: () => {
      client.invalidateQueries({ queryKey: ['invoiceLogs'] });
      client.invalidateQueries({ queryKey: ['companyConfigurationV2'] });
    },
  });

  return addActionNameToMutationResult<
    ApiResponse,
    ApiError,
    UpdateInvoiceLogsVariables,
    'updateInvoiceLogs'
  >('updateInvoiceLogs', mutation);
}
